import { makeAutoObservable, runInAction } from "mobx";
import { createSwayyOptionValue } from "../api/SwayyOptionsAPI";
import {
  createSwayyProductVariants,
  deleteSwayyProductVariant,
  getSwayyDefaultB2cPriceList,
  getSwayyProductDetail,
  getSwayyVariantPrices,
  isCancelledSwayyRequest,
  listSwayyEligibleOptions,
  listSwayyProductVariants,
  mapSwayySelectedOption,
  normalizeSwayyApiError,
  previewSwayyProductVariants,
  SwayyApiError,
  updateSwayyProductOptions,
  upsertSwayyPrices,
} from "../api/SwayyProductsAPI";
import { SwayyCatalogLoadState } from "../models/common/SwayyCatalogLoadState";
import { SwayyEligibleOption } from "../models/options/SwayyCatalogOption";
import { SwayyVariantPriceDraft } from "../models/pricing/SwayyVariantPrice";
import { SwayySelectedOption } from "../models/variants/SwayyProductOptions";
import { SwayyProductVariant } from "../models/variants/SwayyProductVariant";
import {
  SwayyCreateVariantItem,
  SwayyCreateVariantsResult,
  SwayyVariantPreviewRow,
} from "../models/variants/SwayyVariantCreate";
import {
  validateAddOptionValueForm,
  validateVariantPriceDraft,
} from "../validation/swayyFormSchemas";

export type SwayyAddOptionValueForm = {
  value: string;
  skuCode: string;
  hex: string;
};

export class SwayyProductVariantsStore {
  eligibleOptions: SwayyEligibleOption[] = [];
  selectedOptions: SwayySelectedOption[] = [];
  draftSelectedOptionIds: number[] = [];
  variants: SwayyProductVariant[] = [];
  /** B2C default price list id used for Live readiness. */
  b2cPriceListId: number | null = null;
  /** Draft MRP / sale price keyed by variant id. */
  priceDraftByVariantId: Record<number, SwayyVariantPriceDraft> = {};
  savingPriceVariantId: number | null = null;
  loadState: SwayyCatalogLoadState = "idle";
  errorMessage = "";
  isSavingOptions = false;
  isUpdating = false;

  /** value selection for variant creation */
  selectedValueIdsByOptionId: Record<number, number[]> = {};
  previewRows: SwayyVariantPreviewRow[] = [];
  isPreviewing = false;
  isCreatingVariants = false;
  addValueForm: SwayyAddOptionValueForm = {
    value: "",
    skuCode: "",
    hex: "",
  };
  addValueFormError = "";

  variantsTabLoadedProductId: number | null = null;
  private loadInFlightProductId: number | null = null;
  private abortController?: AbortController;
  private previewDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Options the user has clicked (draft). Values are picked against these. */
  get workingOptions(): SwayyEligibleOption[] {
    const selectedIds = new Set(this.draftSelectedOptionIds);
    return this.eligibleOptions.filter((option) => selectedIds.has(option.id));
  }

  get canSaveOptions(): boolean {
    if (this.isSavingOptions) return false;
    if (this.loadState !== "ready") return false;
    return this.hasUnsavedOptionDraft;
  }

  get canBuildVariants(): boolean {
    if (this.workingOptions.length === 0) return false;
    return this.workingOptions.every(
      (option) => (this.selectedValueIdsByOptionId[option.id]?.length ?? 0) > 0,
    );
  }

  /** True when saved options match the draft and every option has a value. */
  get canPreview(): boolean {
    if (this.workingOptions.length === 0) return false;
    if (this.hasUnsavedOptionDraft) return false;
    return this.workingOptions.every(
      (option) => (this.selectedValueIdsByOptionId[option.id]?.length ?? 0) > 0,
    );
  }

  /** Preview rows that don't already exist → the set we'd actually create. */
  get newVariantPreviewRows(): SwayyVariantPreviewRow[] {
    return this.previewRows.filter((row) => !row.exists);
  }

  /** True when the builder enabled options include ones not yet attached. */
  get hasUnsavedOptionDraft(): boolean {
    const savedIds = new Set(
      this.selectedOptions.map((option) => option.optionId),
    );
    return this.draftSelectedOptionIds.some((id) => !savedIds.has(id));
  }

  /**
   * Combinations from clicked values. Used before options are saved
   * (preview API requires attached product_options).
   */
  get pendingComboRows(): SwayyVariantPreviewRow[] {
    if (!this.canBuildVariants) return [];
    const groups = this.workingOptions.map((option) => {
      const selectedIds = new Set(
        this.selectedValueIdsByOptionId[option.id] ?? [],
      );
      return {
        optionId: option.id,
        values: option.values.filter((value) => selectedIds.has(value.id)),
      };
    });
    if (groups.some((group) => group.values.length === 0)) return [];

    let rows: { combo: Record<string, number>; names: string[] }[] = [
      { combo: {}, names: [] },
    ];
    for (const group of groups) {
      const next: { combo: Record<string, number>; names: string[] }[] = [];
      for (const row of rows) {
        for (const value of group.values) {
          next.push({
            combo: { ...row.combo, [String(group.optionId)]: value.id },
            names: [...row.names, value.name],
          });
        }
      }
      rows = next;
    }
    return rows.map((row) => ({
      combo: row.combo,
      label: row.names.join(" / "),
      proposedSku: "",
      exists: false,
    }));
  }

  get displayComboRows(): SwayyVariantPreviewRow[] {
    if (this.canPreview && this.previewRows.length > 0) {
      return this.previewRows;
    }
    return this.pendingComboRows;
  }

  toggleOptionSelection(optionId: number): void {
    const enabled = this.draftSelectedOptionIds.includes(optionId);
    if (enabled) {
      this.draftSelectedOptionIds = this.draftSelectedOptionIds.filter(
        (id) => id !== optionId,
      );
      const nextValues = { ...this.selectedValueIdsByOptionId };
      delete nextValues[optionId];
      this.selectedValueIdsByOptionId = nextValues;
      this.previewRows = [];
      return;
    }
    this.draftSelectedOptionIds = [...this.draftSelectedOptionIds, optionId];
  }

  updateAddValueForm<K extends keyof SwayyAddOptionValueForm>(
    key: K,
    value: SwayyAddOptionValueForm[K],
  ): void {
    this.addValueForm = { ...this.addValueForm, [key]: value };
    if (this.addValueFormError) this.addValueFormError = "";
  }

  resetAddValueForm(): void {
    this.addValueForm = { value: "", skuCode: "", hex: "" };
    this.addValueFormError = "";
  }

  validateAddValueForm(isSwatch: boolean): boolean {
    const validated = validateAddOptionValueForm(this.addValueForm, isSwatch);
    if (!validated.ok) {
      this.addValueFormError = validated.message;
      return false;
    }
    this.addValueFormError = "";
    return true;
  }

  /** Caption under Variants list for Live readiness. */
  get pricingCaption(): string {
    if (this.variants.length === 0) return "";
    if (this.b2cPriceListId == null) {
      return "No default B2C price list found. Create one in catalog setup.";
    }
    const active = this.variants.filter((variant) => variant.active);
    if (active.length === 0) {
      return "At least one active variant is required to publish.";
    }
    const hasPriced = active.some((variant) => {
      const draft = this.priceDraftByVariantId[variant.id];
      const mrp = Number(draft?.mrp);
      const sale = Number(draft?.salePrice);
      return Number.isFinite(mrp) && mrp > 0 && Number.isFinite(sale) && sale > 0;
    });
    if (!hasPriced) {
      return "Set MRP and sale price on at least one active variant before publishing.";
    }
    return "";
  }

  updatePriceDraft(
    variantId: number,
    field: keyof SwayyVariantPriceDraft,
    value: string,
  ): void {
    const current = this.priceDraftByVariantId[variantId] ?? {
      mrp: "",
      salePrice: "",
    };
    this.priceDraftByVariantId = {
      ...this.priceDraftByVariantId,
      [variantId]: { ...current, [field]: value },
    };
  }

  private emptyPriceDrafts(
    variants: SwayyProductVariant[],
  ): Record<number, SwayyVariantPriceDraft> {
    const next: Record<number, SwayyVariantPriceDraft> = {};
    for (const variant of variants) {
      next[variant.id] = { mrp: "", salePrice: "" };
    }
    return next;
  }

  private async loadPricesForVariants(
    variants: SwayyProductVariant[],
    priceListId: number,
    signal?: AbortSignal,
  ): Promise<Record<number, SwayyVariantPriceDraft>> {
    const drafts = this.emptyPriceDrafts(variants);
    await Promise.all(
      variants.map(async (variant) => {
        const prices = await getSwayyVariantPrices(variant.id, signal);
        const match =
          prices.find((price) => price.priceListId === priceListId) ??
          prices[0];
        if (match) {
          drafts[variant.id] = {
            mrp: String(match.mrp),
            salePrice: String(match.salePrice),
          };
        }
      }),
    );
    return drafts;
  }

  async saveVariantPrice(
    variantId: number,
  ): Promise<SwayyApiError | null> {
    if (this.b2cPriceListId == null) {
      return new SwayyApiError(
        "No default B2C price list found.",
        400,
      );
    }
    const draft = this.priceDraftByVariantId[variantId] ?? {
      mrp: "",
      salePrice: "",
    };
    const validated = validateVariantPriceDraft(draft);
    if (!validated.ok) {
      return new SwayyApiError(validated.message, 400);
    }

    this.savingPriceVariantId = variantId;
    try {
      const saved = await upsertSwayyPrices(this.b2cPriceListId, [
        {
          variant_id: variantId,
          mrp: validated.mrp,
          sale_price: validated.salePrice,
        },
      ]);
      const match = saved.find((price) => price.variantId === variantId);
      runInAction(() => {
        if (match) {
          this.priceDraftByVariantId = {
            ...this.priceDraftByVariantId,
            [variantId]: {
              mrp: String(match.mrp),
              salePrice: String(match.salePrice),
            },
          };
        }
      });
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to save price.");
    } finally {
      runInAction(() => {
        this.savingPriceVariantId = null;
      });
    }
  }

  async loadVariantsTab(
    productId: number,
    options: { force?: boolean } = {},
  ): Promise<SwayyApiError | null> {
    const { force = false } = options;
    if (
      !force &&
      this.variantsTabLoadedProductId === productId &&
      this.loadState === "ready"
    ) {
      return null;
    }
    if (this.loadInFlightProductId === productId) {
      return null;
    }

    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.loadInFlightProductId = productId;

    runInAction(() => {
      this.loadState = "loading";
      this.errorMessage = "";
    });

    try {
      const [detail, eligible, variants, b2cList] = await Promise.all([
        getSwayyProductDetail(productId, controller.signal),
        listSwayyEligibleOptions(productId, controller.signal),
        listSwayyProductVariants(productId, controller.signal),
        getSwayyDefaultB2cPriceList(controller.signal),
      ]);

      if (controller.signal.aborted) return null;

      const selected = (detail.selected_options || [])
        .map(mapSwayySelectedOption)
        .filter((option): option is SwayySelectedOption => option !== null);

      const priceDrafts =
        b2cList != null
          ? await this.loadPricesForVariants(
              variants,
              b2cList.id,
              controller.signal,
            )
          : this.emptyPriceDrafts(variants);

      if (controller.signal.aborted) return null;

      runInAction(() => {
        this.eligibleOptions = eligible;
        this.selectedOptions = selected;
        this.draftSelectedOptionIds = [];
        this.selectedValueIdsByOptionId = {};
        this.previewRows = [];
        this.variants = variants;
        this.b2cPriceListId = b2cList?.id ?? null;
        this.priceDraftByVariantId = priceDrafts;
        this.loadState = "ready";
        this.variantsTabLoadedProductId = productId;
        this.loadInFlightProductId = null;
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error) || controller.signal.aborted) {
        runInAction(() => {
          if (this.loadInFlightProductId === productId) {
            this.loadInFlightProductId = null;
          }
        });
        return null;
      }
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load variants and options. Please try again.",
      );
      runInAction(() => {
        this.loadState = "error";
        this.errorMessage = apiError.message;
        this.loadInFlightProductId = null;
      });
      return apiError;
    }
  }

  private async reloadEligibleOptions(productId: number): Promise<void> {
    const eligible = await listSwayyEligibleOptions(productId);
    runInAction(() => {
      this.eligibleOptions = eligible;
    });
  }

  async saveProductOptions(
    productId: number,
  ): Promise<SwayyApiError | null> {
    if (!this.canSaveOptions) return null;
    this.isSavingOptions = true;
    try {
      const attachIds = new Set([
        ...this.selectedOptions.map((option) => option.optionId),
        ...this.draftSelectedOptionIds,
      ]);
      const payload = this.eligibleOptions
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => attachIds.has(option.id))
        .map(({ option, index }) => ({
          option_id: option.id,
          position: index,
        }));

      const selected = await updateSwayyProductOptions(productId, payload);
      runInAction(() => {
        this.selectedOptions = selected;
        this.isSavingOptions = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to save product options.",
      );
      runInAction(() => {
        this.isSavingOptions = false;
      });
      return apiError;
    }
  }

  /** Add a value on an eligible option (catalog value; like Swageazy). */
  async createOptionValue(
    productId: number,
    optionId: number,
    isSwatch: boolean,
  ): Promise<SwayyApiError | null> {
    this.isUpdating = true;
    try {
      const value = this.addValueForm.value.trim();
      const skuCode = this.addValueForm.skuCode.trim();
      const hex = this.addValueForm.hex.trim();
      await createSwayyOptionValue(optionId, {
        value,
        sku_code: skuCode,
        meta: isSwatch && hex ? { hex } : null,
      });
      await this.reloadEligibleOptions(productId);
      runInAction(() => {
        this.isUpdating = false;
        this.resetAddValueForm();
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to create option value.",
      );
      runInAction(() => {
        this.isUpdating = false;
      });
      return apiError;
    }
  }

  /** Soft-delete a product variant. */
  async deleteVariant(
    productId: number,
    variantId: number,
  ): Promise<SwayyApiError | null> {
    this.isUpdating = true;
    try {
      await deleteSwayyProductVariant(variantId);
      const variants = await listSwayyProductVariants(productId);
      runInAction(() => {
        this.variants = variants;
        this.previewRows = [];
        this.isUpdating = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to delete variant.",
      );
      runInAction(() => {
        this.isUpdating = false;
      });
      return apiError;
    }
  }

  toggleOptionValue(optionId: number, valueId: number): void {
    if (!this.draftSelectedOptionIds.includes(optionId)) {
      this.draftSelectedOptionIds = [
        ...this.draftSelectedOptionIds,
        optionId,
      ];
    }
    const current = this.selectedValueIdsByOptionId[optionId] ?? [];
    const idx = current.indexOf(valueId);
    if (idx === -1) {
      this.selectedValueIdsByOptionId = {
        ...this.selectedValueIdsByOptionId,
        [optionId]: [...current, valueId],
      };
    } else {
      this.selectedValueIdsByOptionId = {
        ...this.selectedValueIdsByOptionId,
        [optionId]: current.filter((id) => id !== valueId),
      };
    }
  }

  schedulePreview(productId: number): void {
    if (this.previewDebounceTimer !== null) {
      clearTimeout(this.previewDebounceTimer);
    }
    // Keep last preview while fetching; only clear when selection can't preview.
    if (!this.canPreview) {
      this.previewRows = [];
      return;
    }
    this.previewDebounceTimer = setTimeout(() => {
      void this.previewVariants(productId);
    }, 300);
  }

  async previewVariants(productId: number): Promise<SwayyApiError | null> {
    if (!this.canPreview) return null;
    const selections = this.workingOptions.map((option) => ({
      option_id: option.id,
      value_ids: this.selectedValueIdsByOptionId[option.id] ?? [],
    }));
    runInAction(() => {
      this.isPreviewing = true;
    });
    try {
      const rows = await previewSwayyProductVariants(productId, selections);
      runInAction(() => {
        this.previewRows = rows;
        this.isPreviewing = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to preview variants.",
      );
      runInAction(() => {
        this.isPreviewing = false;
      });
      return apiError;
    }
  }

  /**
   * One user action: attach draft options if needed, preview, create new combos.
   */
  async saveAndCreateVariants(
    productId: number,
  ): Promise<{
    error: SwayyApiError | null;
    result: SwayyCreateVariantsResult | null;
  }> {
    if (!this.canBuildVariants) {
      return {
        error: new SwayyApiError("Pick values for each selected option."),
        result: null,
      };
    }
    if (this.hasUnsavedOptionDraft) {
      const optionsError = await this.saveProductOptions(productId);
      if (optionsError) return { error: optionsError, result: null };
    }
    const previewError = await this.previewVariants(productId);
    if (previewError) return { error: previewError, result: null };
    if (this.newVariantPreviewRows.length === 0) {
      return {
        error: new SwayyApiError("Nothing new to create."),
        result: null,
      };
    }
    return this.createVariants(productId);
  }

  async createVariants(
    productId: number,
  ): Promise<{
    error: SwayyApiError | null;
    result: SwayyCreateVariantsResult | null;
  }> {
    if (this.newVariantPreviewRows.length === 0) {
      return { error: null, result: null };
    }
    const variantItems: SwayyCreateVariantItem[] = this.newVariantPreviewRows.map(
      (row) => ({ combo: row.combo }),
    );
    runInAction(() => {
      this.isCreatingVariants = true;
    });
    try {
      const result = await createSwayyProductVariants(productId, variantItems);
      if (result.createdCount > 0) {
        const variants = await listSwayyProductVariants(productId);
        const priceDrafts =
          this.b2cPriceListId != null
            ? await this.loadPricesForVariants(variants, this.b2cPriceListId)
            : this.emptyPriceDrafts(variants);
        runInAction(() => {
          this.variants = variants;
          this.priceDraftByVariantId = priceDrafts;
          this.previewRows = [];
          this.selectedValueIdsByOptionId = {};
          this.draftSelectedOptionIds = [];
          this.isCreatingVariants = false;
        });
      } else {
        // Total failure — keep selection so user can retry
        runInAction(() => {
          this.isCreatingVariants = false;
        });
      }
      return { error: null, result };
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to create variants.",
      );
      runInAction(() => {
        this.isCreatingVariants = false;
      });
      return { error: apiError, result: null };
    }
  }

  /** Mark variants tab stale so the next open refetches (e.g. after category save). */
  invalidateTabCache(): void {
    this.variantsTabLoadedProductId = null;
  }

  dispose(): void {
    this.abortController?.abort();
    this.abortController = undefined;
    this.loadInFlightProductId = null;
    this.variantsTabLoadedProductId = null;
    if (this.previewDebounceTimer !== null) {
      clearTimeout(this.previewDebounceTimer);
    }
  }
}
